import { PipelineTab } from '../../../shared/model/pipeline-tab';
import {
  MedicalCase,
  MedicalCasePhaseDocumentState,
  MedicalCasePhases,
  MedicalCaseProcessedContent,
  MedicalCaseProcessingPhase,
} from '../../../domain/medical-case/model';

export type MedicalCasePipelineFlow = 'preparation' | 'wayback';

export type PreparationStepKey =
  | 'upload'
  | 'preview'
  | 'digitization'
  | 'translate'
  | 'anonymize'
  | 'summary'
  | 'glossary'
  | 'final-report'
  | 'deanonymize'
  | 'package';

export type StepCompletionState = Record<PreparationStepKey, boolean>;
export type StepProcessingStatus =
  | 'idle'
  | 'processing'
  | 'completed'
  | 'error';
export type StepProcessingState = Record<
  PreparationStepKey,
  StepProcessingStatus
>;
export type StepContentState = Partial<
  Record<PreparationStepKey, MedicalCaseProcessedContent>
>;

export const INITIAL_STEP_COMPLETION: StepCompletionState = {
  upload: false,
  preview: false,
  digitization: false,
  translate: false,
  anonymize: false,
  summary: false,
  glossary: false,
  'final-report': false,
  deanonymize: false,
  package: false,
};

export const INITIAL_STEP_PROCESSING: StepProcessingState = {
  upload: 'idle',
  preview: 'idle',
  digitization: 'idle',
  translate: 'idle',
  anonymize: 'idle',
  summary: 'idle',
  glossary: 'idle',
  'final-report': 'idle',
  deanonymize: 'idle',
  package: 'idle',
};

export const ALWAYS_DISABLED_STEPS: ReadonlySet<PreparationStepKey> =
  new Set<PreparationStepKey>();

export const STEP_ORDER: PreparationStepKey[] = [
  'upload',
  'preview',
  'digitization',
  'translate',
  'anonymize',
  'summary',
  'glossary',
  'final-report',
  'deanonymize',
  'package',
];

export type ProcessingPhasePairKey =
  `${MedicalCaseProcessingPhase}->${MedicalCaseProcessingPhase}`;

export const FIRST_OPINION_COMPLETION_REQUIRED_PHASES: readonly MedicalCaseProcessingPhase[] = [
  'MEDICAL_RECORD_INIT',
  'MEDICAL_RECORD_DIGITIZATION',
  'MEDICAL_RECORD_TRANSLATION',
  'MEDICAL_RECORD_ANONYMIZATION',
  'MEDICAL_RECORD_SUMMARY',
  'MEDICAL_RECORD_PACKAGING',
] as const;

const FIRST_OPINION_INTERMEDIATE_PHASES =
  FIRST_OPINION_COMPLETION_REQUIRED_PHASES.filter(
    phase => phase !== 'MEDICAL_RECORD_PACKAGING'
  );

function isSecondOpinionIntermediateState(
  state: MedicalCasePhaseDocumentState | null | undefined
): boolean {
  return (
    state === 'COMPLETED' ||
    state === 'CONFIRMED' ||
    state === 'FAILED'
  );
}

export function isFirstOpinionComplete(
  phases: MedicalCasePhases | null | undefined
): boolean {
  if (phases == null) {
    return false;
  }

  const packagingState = phases.MEDICAL_RECORD_PACKAGING?.state;
  if (packagingState !== 'COMPLETED' && packagingState !== 'CONFIRMED') {
    return false;
  }

  return FIRST_OPINION_INTERMEDIATE_PHASES.every(phase =>
    isSecondOpinionIntermediateState(phases[phase]?.state)
  );
}

export interface StepDocumentViewEntry {
  originalDocumentId: string;
  isActive: boolean;
  lastAvailablePhase: MedicalCaseProcessingPhase | null;
  activeVersionId: string | null;
  activeVersionFileId: string | null;
  phaseState: MedicalCasePhaseDocumentState | null;
}

export type ProcessingStep = PreparationStepKey | null;

export interface StepOutdatedRule {
  dependencyPhases?: MedicalCaseProcessingPhase[];
  nullGuardPhase?: MedicalCaseProcessingPhase;
}

export type FileIdResolver = (medicalCase: MedicalCase) => boolean;

export interface FlowPipelineDefinition {
  readonly tabs: readonly Omit<PipelineTab, 'disabled'>[];
  readonly stepOrder: readonly PreparationStepKey[];
  readonly defaultUnlockedSteps?: readonly PreparationStepKey[];
  readonly processingPairToStepMap: Readonly<Partial<Record<ProcessingPhasePairKey, PreparationStepKey>>>;
  readonly phaseToStepMap: Readonly<Partial<Record<MedicalCaseProcessingPhase, PreparationStepKey>>>;
  readonly stepToPhaseMap: Readonly<Partial<Record<PreparationStepKey, MedicalCaseProcessingPhase>>>;
  readonly completionPhases: ReadonlyArray<{ step: PreparationStepKey; phase: MedicalCaseProcessingPhase }>;
  readonly fileIdResolvers: Readonly<Partial<Record<PreparationStepKey, FileIdResolver>>>;
  readonly outdatedRules?: Readonly<Partial<Record<PreparationStepKey, StepOutdatedRule>>>;
  readonly documentPhaseOrder?: readonly MedicalCaseProcessingPhase[];
  readonly allPhaseOrder?: readonly MedicalCaseProcessingPhase[];
}

// Re-export flow-specific definitions from dedicated files
export {
  PREPARATION_TABS,
  PREPARATION_STEP_ORDER,
  PREPARATION_PROCESSING_PAIR_TO_STEP_MAP,
  PREPARATION_PHASE_TO_STEP_MAP,
  PREPARATION_STEP_TO_PHASE_MAP,
} from './preparation-pipeline.definition';

export {
  WAYBACK_TABS,
  WAYBACK_STEP_ORDER,
  WAYBACK_PROCESSING_PAIR_TO_STEP_MAP,
  WAYBACK_PHASE_TO_STEP_MAP,
  WAYBACK_STEP_TO_PHASE_MAP,
} from './wayback-pipeline.definition';

// Backward-compatible aliases
export { PREPARATION_TABS as BASE_PREPARATION_TABS } from './preparation-pipeline.definition';
export { WAYBACK_TABS as BASE_WAYBACK_TABS } from './wayback-pipeline.definition';
export { PREPARATION_PROCESSING_PAIR_TO_STEP_MAP as PROCESSING_PAIR_TO_STEP_MAP } from './preparation-pipeline.definition';
export { PREPARATION_PHASE_TO_STEP_MAP as PHASE_TO_STEP_MAP } from './preparation-pipeline.definition';
export { PREPARATION_STEP_TO_PHASE_MAP as STEP_TO_PHASE_MAP } from './preparation-pipeline.definition';

export { PREPARATION_PIPELINE_DEFINITION } from './preparation-pipeline.definition';
export { WAYBACK_PIPELINE_DEFINITION } from './wayback-pipeline.definition';
